import React from 'react';
import { 
  X, 
  AlertTriangle, 
  Battery, 
  AlertCircle, 
  Calendar, 
  CheckCircle, 
  Bell,
  BellOff,
  WifiOff
} from 'lucide-react';
import { useNotifications } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';

/**
 * NotificationCenter Component
 * 
 * Displays a list of active notifications with:
 * - Priority-based styling (HIGH=red, MEDIUM=yellow, LOW=blue)
 * - Type-based icons
 * - Action buttons
 * - Auto-dismiss countdown
 * - Compact mobile design
 */
const NotificationCenter = () => {
  const { user } = useAuth();
  const { notifications, removeNotification, clearAll, unreadCount } = useNotifications();
  
  // Only show pop-ups for customers, and only for notifications with duration > 0 (temporary pop-ups)
  // Admins should not see any pop-ups - they should only see notifications in the bell icon
  if (user?.role !== 'CUSTOMER') {
    return null;
  }
  
  // Filter to only show notifications that should appear as pop-ups (duration > 0)
  const popupNotifications = notifications.filter(notif => notif.duration > 0);
  
  if (popupNotifications.length === 0) {
    return null;
  }

  // Get icon for notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'BATTERY_LOW':
        return <Battery className="h-5 w-5" />;
      case 'ROBOT_OFFLINE':
        return <WifiOff className="h-5 w-5" />;
      case 'CRITICAL_STATUS':
        return <AlertTriangle className="h-5 w-5" />;
      case 'JOB_SCHEDULED':
      case 'JOB_SUBMITTED':
      case 'JOB_PENDING_APPROVAL':
      case 'JOB_APPROVED':
      case 'JOB_REJECTED':
      case 'JOB_COMPLETED':
        return <Calendar className="h-5 w-5" />;
      default:
        return <AlertCircle className="h-5 w-5" />;
    }
  };

  // Get styling based on notification type and priority
  const getNotificationStyles = (type, priority) => {
    // Color coding for job-related notifications
    if (type === 'JOB_APPROVED') {
      return {
        border: 'border-green-500',
        bg: 'bg-green-50',
        text: 'text-green-900',
        iconBg: 'bg-green-100',
        iconColor: 'text-green-600',
        badge: 'bg-green-600'
      };
    }
    
    if (type === 'JOB_REJECTED') {
      return {
        border: 'border-red-500',
        bg: 'bg-red-50',
        text: 'text-red-900',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
        badge: 'bg-red-600'
      };
    }
    
    if (type === 'JOB_PENDING_APPROVAL') {
      return {
        border: 'border-yellow-500',
        bg: 'bg-yellow-50',
        text: 'text-yellow-900',
        iconBg: 'bg-yellow-100',
        iconColor: 'text-yellow-600',
        badge: 'bg-yellow-600'
      };
    }
    
    // Default priority-based styling for other notifications
    switch (priority) {
      case 'HIGH':
        return {
          border: 'border-red-500',
          bg: 'bg-red-50',
          text: 'text-red-900',
          iconBg: 'bg-red-100',
          iconColor: 'text-red-600',
          badge: 'bg-red-600'
        };
      case 'MEDIUM':
        return {
          border: 'border-yellow-500',
          bg: 'bg-yellow-50',
          text: 'text-yellow-900',
          iconBg: 'bg-yellow-100',
          iconColor: 'text-yellow-600',
          badge: 'bg-yellow-600'
        };
      case 'LOW':
        return {
          border: 'border-blue-500',
          bg: 'bg-blue-50',
          text: 'text-blue-900',
          iconBg: 'bg-blue-100',
          iconColor: 'text-blue-600',
          badge: 'bg-blue-600'
        };
      default:
        return {
          border: 'border-gray-500',
          bg: 'bg-gray-50',
          text: 'text-gray-900',
          iconBg: 'bg-gray-100',
          iconColor: 'text-gray-600',
          badge: 'bg-gray-600'
        };
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (seconds < 60) {
      return 'Just now';
    } else if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else if (days < 7) {
      return `${days}d ago`;
    } else {
      // Show full date for older notifications
      return new Date(timestamp).toLocaleString('en-US', { 
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  if (notifications.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-14 sm:top-16 left-2 right-2 sm:left-4 sm:right-auto md:left-4 md:right-auto lg:left-auto lg:right-4 z-[9999] w-[calc(100vw-1rem)] sm:w-[320px] md:w-[340px] lg:w-96">
      {/* Notification Container */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 max-h-[60vh] sm:max-h-[70vh] md:max-h-[80vh] lg:max-h-[600px] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-3 sm:px-4 py-2.5 sm:py-3 flex items-center justify-between z-10">
          <div className="flex items-center space-x-2">
            <Bell className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
            <h3 className="text-xs sm:text-sm font-semibold text-gray-900">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-1.5 sm:ml-2 inline-flex items-center justify-center w-4 h-4 sm:w-5 sm:h-5 bg-red-600 text-white text-[10px] sm:text-xs font-bold rounded-full">
                  {unreadCount}
                </span>
              )}
            </h3>
          </div>
          <button
            onClick={clearAll}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title="Clear all notifications"
          >
            <BellOff className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        {/* Notifications List */}
        <div className="divide-y divide-gray-100">
          {popupNotifications.map((notification) => {
            const styles = getNotificationStyles(notification.type, notification.priority);
            
            return (
              <div
                key={notification.id}
                className={`px-3 sm:px-4 py-2.5 sm:py-3 border-l-4 ${styles.border} ${styles.bg} hover:${styles.bg.replace('50', '100')} transition-colors`}
              >
                <div className="flex items-start space-x-2 sm:space-x-3">
                  {/* Icon */}
                  <div className={`flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${styles.iconBg} flex items-center justify-center ${styles.iconColor}`}>
                    {getNotificationIcon(notification.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <h4 className={`text-xs sm:text-sm font-semibold ${styles.text} mb-0.5 sm:mb-1 truncate`}>
                          {notification.title}
                        </h4>
                        <p className={`text-xs sm:text-sm ${styles.text.replace('900', '700')} line-clamp-2`}>
                          {notification.message}
                        </p>
                        
                        {/* Metadata */}
                        {notification.metadata.robotName && (
                          <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 sm:mt-1">
                            Robot: {notification.metadata.robotName}
                          </p>
                        )}
                        
                        {/* Timestamp */}
                        <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                          {formatTimestamp(notification.timestamp)}
                        </p>
                      </div>

                      {/* Close Button */}
                      <button
                        onClick={() => removeNotification(notification.id)}
                        className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors ml-1.5 sm:ml-2"
                        title="Dismiss"
                      >
                        <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </div>

                    {/* Action Buttons */}
                    {notification.actions && notification.actions.length > 0 && (
                      <div className="flex items-center space-x-1.5 sm:space-x-2 mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-gray-200">
                        {notification.actions.map((action, index) => (
                          <button
                            key={index}
                            onClick={() => {
                              action.onClick();
                              if (action.dismissOnClick !== false) {
                                removeNotification(notification.id);
                              }
                            }}
                            className={`text-[10px] sm:text-xs font-medium px-2 sm:px-3 py-1 sm:py-1.5 rounded-md transition-colors ${
                              action.primary
                                ? `${styles.badge} text-white hover:opacity-90`
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {popupNotifications.length > 3 && (
          <div className="sticky bottom-0 bg-white border-t border-gray-200 px-3 sm:px-4 py-1.5 sm:py-2 text-center">
            <button
              onClick={clearAll}
              className="text-[10px] sm:text-xs text-gray-600 hover:text-gray-900 transition-colors"
            >
              Clear all ({popupNotifications.length})
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter;

